import React, { useState, useEffect, useRef, useCallback } from 'react'
import { useNavigate, useParams } from 'react-router'
import { observer } from 'mobx-react-lite'
import classNames from 'classnames'

// evertel
import { DocumentServiceProvider } from '@evertel/service'
import { useService } from '@evertel/di'
import { Document, WidgetsProvider } from '@evertel/schema-parser'
import { DocumentCreateController, DocumentRecentTemplatesController, EverdocMediaUplaodError, hasDocumentDataChanges } from '@evertel/document'
import { useUnsavedChangesGuard } from '../utils'
import { WriteWidgets } from './schema-widgets'
import { DocumentFooter, DocumentIcon, DocumentPlaceholder } from './elements'
import {
    Button,
    Text,
    InfoBox,
    PageBody,
    Breadcrumb,
    Input,
    Card,
    CardHeader,
    CardBody,
    CardHeaderActions,
    useUI,
    CardFooter,
    Icon
} from '@evertel/web/ui'
import { SessionService } from '@evertel/session/SessionService'
import { APIDataDocumentSchema } from '@evertel/types'


const DocumentCreate = observer(() => {

    const { schemaId } = useParams()
    const { addToast } = useUI()
    const navigate = useNavigate()

    const [isLoading, setIsLoading] = useState(true)
    const [isSaving, setIsSaving] = useState(false)
    const [hasChanged, setHasChanged] = useState(false)
    const [documentName, setDocumentName] = useState('')
    const [documentNameError, setDocumentNameError] = useState(false)

    // Prompts on ANY navigation away (links, back button, refresh) while there are unsaved changes
    const { allowNavigation } = useUnsavedChangesGuard(hasChanged)


    const documentCreateController = useService(DocumentCreateController, [schemaId])
    const documentRecentTemplatesController = useService(DocumentRecentTemplatesController, [])
    const sessionService = useService(SessionService, [])

    useEffect(() => {
        (async () => {
            if (!schemaId) return

            setIsLoading(true)
            await documentCreateController.init(
                sessionService.selectedDepartmentId,
                Number(schemaId),
                'web'
            )
            setDocumentName(documentCreateController.name)
            setIsLoading(false)
        })()
    }, [documentCreateController, sessionService])

    const onChangeName = (e: any) => {
        setDocumentNameError(false)
        setDocumentName(e?.target?.value)
        setHasChanged(true)
        documentCreateController.setName(e?.target?.value)
    }

    const onSave = async () => {
        setIsSaving(true)
        try {

            if (!documentName.trim()) {
                setDocumentNameError(true)
                throw new Error('Document name is required')
            }

            const { id } = await documentCreateController.create()

            documentRecentTemplatesController.recordTemplateUsed(Number(schemaId)).catch(console.error)

            setHasChanged(false)
            allowNavigation()
            navigate(`/everdocs/view/${id}`)

        } catch (error: any) {
            if (error instanceof EverdocMediaUplaodError) {
                documentRecentTemplatesController.recordTemplateUsed(Number(schemaId)).catch(console.error)

                addToast({
                    color: 'warning',
                    title: 'Be aware',
                    message: `Document created with some media issues. \n ${error.message}`
                })

                setHasChanged(false)
                allowNavigation()
                navigate(`/everdocs/view/${error?.document?.id}`)

            } else {
                addToast({
                    color: 'danger',
                    title: 'Something went wrong',
                    message: error.message
                })
            }

            return
        } finally {
            setIsSaving(false)
        }
    }

    const onChange = useCallback((value: Record<string, any>): void => {
        // same rule as DocumentEdit: the parser emits defaults on mount, which isn't a user edit
        if (hasDocumentDataChanges(documentCreateController.data, value)) setHasChanged(true)
        documentCreateController.updateData(value)
    }, [documentCreateController])

    if (isLoading) {
        return (
            <DocumentPlaceholder />
        )
    }

    return (
        <PageBody>
            <Breadcrumb
                items={[
                    { label: 'EverDocs', onClick: () => navigate('/everdocs') },
                    { label: 'Templates', onClick: () => navigate('/everdocs/templates') },
                    { label: 'Create New EverDoc', active: true }
                ]}
            />
            <h3>
                Creating: <b>{documentCreateController?.documentSchema?.name}</b>
            </h3>
            <p>
                {documentCreateController?.documentSchema?.description}
            </p>
            <p className="text-muted">
                Fill in at least the required (*) fields and click "Save" to create the document
            </p>
            {(!!documentCreateController.errors) &&
                <InfoBox color="danger" className="mb-3 py-2">
                    <Icon name="circle-xmark" type="solid" className="me-2" />
                    <Text>
                        {`Please review the document, there ${(documentCreateController.errorsCount === 1) ?
                            'is 1 error' : `are ${documentCreateController.errorsCount} errors`}`}
                    </Text>
                </InfoBox>
            }
            <Card>
                <CardHeader className="flex-wrap gap-2 align-items-start document-name-header py-4">
                    <div className="d-flex align-items-center" style={{ minWidth: '80%' }}>
                        <DocumentIcon
                            schema={documentCreateController?.documentSchema as APIDataDocumentSchema}
                            width={40}
                            height={44}
                        />
                        <div className="w-100 ms-2 position-relative">
                            <Input
                                style={{ borderColor: documentNameError ? 'red' : undefined }}
                                className={classNames(
                                    'form-control h3',
                                    { 'is-invalid': documentNameError }
                                )}
                                value={documentName}
                                required
                                onChange={onChangeName}
                                placeholder="Enter title"
                                onFocus={(e) => e.target.setSelectionRange(e.target.value.length, e.target.value.length)}
                            />
                            {documentNameError &&
                                <strong className="invalid-feedback">
                                    Document Name is required.
                                </strong>
                            }
                        </div>
                    </div>
                    <CardHeaderActions>
                        <Button
                            color="primary"
                            loading={isSaving}
                            onClick={onSave}>
                            <Text
                                bold>
                                Save
                            </Text>
                        </Button>
                    </CardHeaderActions>
                </CardHeader>
                <CardBody>
                    <DocumentServiceProvider services={[{ id: 'meta', value: {} }]}>
                        <WidgetsProvider value={WriteWidgets}>
                            <Document
                                key={schemaId}
                                layout="full"
                                schema={documentCreateController.documentSchema?.schema}
                                schemaId={documentCreateController.documentSchemaId}
                                value={documentCreateController.data}
                                onChange={onChange}
                                errors={documentCreateController.errors}
                            />
                        </WidgetsProvider>
                    </DocumentServiceProvider>
                </CardBody>
                <CardFooter className="d-flex flex-wrap align-items-center justify-content-between">
                    <div>
                        <span aria-hidden="true"><Icon size="xs" name="asterisk" color="danger" faProps={{title: 'Required'}}/> = required</span>
                    </div>
                    <DocumentFooter
                        // document={document}
                        schema={documentCreateController.documentSchema}
                    />
                </CardFooter>
            </Card>
        </PageBody>
    )
})

export {
    DocumentCreate
}
